import { Trans } from '@lingui/react/macro';
import React, { PropsWithChildren } from 'react';

import { assertExhaustive } from 'backend-client-web';
import {
  ActionButton,
  PushState,
  SVGReactComponent,
  unlockDeviceTypeForMethods,
  UnlockMethods,
  UnlockLabel,
  useMousetrap,
  useUIMode,
  AutoFadeTransition,
} from 'client-web-sdk';

import Modal from 'src/components/modal/Modal';
import { RequestUserConfirmationResult } from 'src/util/useRequestUserConfirmation';

import styles from './ConfirmationModal.module.css';

type ActionSummaryProps = PropsWithChildren<{
  state: 'actionSummary';
  illustration?: SVGReactComponent;
  headline: string;
  action: string;
  actionDisabled?: boolean;
  actionType?: 'danger' | 'userConfirmation';
  confirm: () => void;
  close: () => void;
}>;

type UserConfirmationProps = PropsWithChildren<{
  state: 'userConfirmation';
  result: RequestUserConfirmationResult | undefined;
  description: string;
  unlockMethods: UnlockMethods;
  close: () => void;
  cancel: () => void;
  retry: () => void;
}>;

export type ConfirmationModalProps = {
  isOpen: boolean;
  width?: string;
  loading?: boolean;
} & (ActionSummaryProps | UserConfirmationProps);

const MIN_WIDTH = '30rem';
const MAX_WIDTH = '40rem';
const USER_CONFIRMATION_MIN_HEIGHT = '26rem';
const MIN_HEIGHT = '8rem';

export default function ConfirmationModal({
  loading,
  width,
  isOpen,
  ...contentProps
}: ConfirmationModalProps) {
  const widthProps =
    width ?
      {
        width,
        minWidth: contentProps.state === 'userConfirmation' ? MIN_WIDTH : undefined,
      }
    : {
        minWidth: MIN_WIDTH,
        maxWidth: MAX_WIDTH,
      };

  return (
    <Modal
      className={styles.ConfirmationModal}
      contentClassName={styles.ConfirmationModalContent}
      onRequestClose={contentProps.close}
      isOpen={isOpen}
      height={contentProps.state === 'userConfirmation' ? USER_CONFIRMATION_MIN_HEIGHT : undefined}
      minHeight={MIN_HEIGHT}
      loading={loading}
      {...widthProps}
    >
      <AutoFadeTransition className={styles.Transition}>
        {contentProps.state === 'actionSummary' ?
          <ActionSummary {...contentProps} />
        : contentProps.state === 'userConfirmation' ?
          <UserConfirmation {...contentProps} />
        : assertExhaustive(contentProps)}
      </AutoFadeTransition>
    </Modal>
  );
}

function ActionSummary({
  illustration: Illustration,
  children,
  headline,
  confirm,
  close,
  actionType,
  action,
  actionDisabled,
}: ActionSummaryProps) {
  useMousetrap('enter', confirm, { deps: [confirm] });
  const uiMode = useUIMode();

  return (
    <div className={styles.ActionSummary}>
      <div className={styles.ActionSummaryInfo}>
        <div className={styles.ActionSummaryInfoContent}>
          {Illustration && <Illustration className={styles.ActionSummaryIllustration} />}
          <div className={styles.Options}>
            <strong className={styles.ActionSummaryHeadline}>{headline}</strong>
            {children}
          </div>
        </div>
      </div>
      <div className={styles.ActionSummaryButtons}>
        <ActionButton buttonStyle="tertiary" onClick={close} className={styles.Cancel}>
          <Trans>Cancel</Trans>
        </ActionButton>
        <ActionButton
          buttonStyle={actionType === 'userConfirmation' ? 'userConfirmation' : 'primary'}
          onClick={confirm}
          className={styles.Confirm}
          disabled={actionDisabled}
          overrideColor={
            actionType === 'danger' ?
              uiMode === 'dark' ?
                'var(--hl-color-red-crayola-800)'
              : 'var(--hl-color-red-crayola-400-m)'
            : undefined
          }
        >
          {action}
        </ActionButton>
      </div>
    </div>
  );
}

function UserConfirmation({
  description,
  result,
  unlockMethods,
  cancel,
  close,
  retry,
}: UserConfirmationProps) {
  return (
    <div className={styles.UserConfirmation}>
      <div>
        <strong className={styles.UserConfirmationHeadline}>
          {result === RequestUserConfirmationResult.accepted ?
            <Trans>Action confirmed</Trans>
          : result === RequestUserConfirmationResult.canceled ?
            <Trans>Action canceled</Trans>
          : result === RequestUserConfirmationResult.timeout ?
            <Trans>Request failed</Trans>
          : result === RequestUserConfirmationResult.rejected ?
            <Trans>Request denied</Trans>
          : result === undefined ?
            <Trans>Confirm action</Trans>
          : assertExhaustive(result)}
        </strong>
      </div>
      <div>
        <strong>
          <Trans>“{description}”</Trans>
        </strong>
      </div>
      <PushState
        state={
          result === RequestUserConfirmationResult.accepted ? 'confirmed'
          : (
            result === RequestUserConfirmationResult.canceled ||
            result === RequestUserConfirmationResult.timeout
          ) ?
            'warning'
          : result === RequestUserConfirmationResult.rejected ?
            'error'
          : result === undefined ?
            'connecting'
          : assertExhaustive(result)
        }
        unlockDeviceType={unlockDeviceTypeForMethods(unlockMethods)}
      />
      <div>
        <div>
          {result === undefined ?
            <UnlockLabel unlockMethods={unlockMethods} />
          : <>&nbsp;</>}
        </div>
      </div>
      {result === undefined ?
        <ActionButton
          buttonStyle="secondary"
          className={styles.UserConfirmationAction}
          onClick={cancel}
        >
          <Trans>Cancel</Trans>
        </ActionButton>
      : canRetry(result) ?
        <ActionButton
          buttonStyle="secondary"
          className={styles.UserConfirmationAction}
          onClick={retry}
        >
          <Trans>Retry</Trans>
        </ActionButton>
      : <ActionButton
          buttonStyle="secondary"
          className={styles.UserConfirmationAction}
          onClick={close}
        >
          <Trans>Close</Trans>
        </ActionButton>
      }
    </div>
  );
}

function canRetry(result: RequestUserConfirmationResult) {
  return (
    result === RequestUserConfirmationResult.rejected ||
    result === RequestUserConfirmationResult.timeout
  );
}
